/**
 * Número Único de Processo (Resolução CNJ nº 65/2008).
 *
 * Formato: `NNNNNNN-DD.AAAA.J.TR.OOOO` — 20 dígitos no total.
 *
 *   NNNNNNN  sequencial por unidade de origem, reiniciado a cada ano
 *   DD       dígito verificador (módulo 97, base 10 — ISO 7064)
 *   AAAA     ano do ajuizamento
 *   J        segmento do Judiciário
 *   TR       tribunal
 *   OOOO     unidade de origem
 */
import { tribunaisCandidatos } from "./tribunais.js";
/** Remove tudo que não for dígito. */
export function apenasDigitos(entrada) {
    return entrada.replace(/\D/g, "");
}
/**
 * Dígito verificador previsto para os 20 dígitos informados.
 *
 * O cálculo segue o módulo 97 base 10 (ISO 7064): reordena-se o número como
 * `NNNNNNN AAAA J TR OOOO`, multiplica-se por 100 e o resto da divisão por 97
 * é subtraído de 98.
 */
export function calcularDigitoVerificador(digitos) {
    const sequencial = digitos.slice(0, 7);
    const ano = digitos.slice(9, 13);
    const segmentoTribunalOrigem = digitos.slice(13, 20);
    const base = BigInt(`${sequencial}${ano}${segmentoTribunalOrigem}`) * 100n;
    const resto = Number(base % 97n);
    return String(98 - resto).padStart(2, "0");
}
export function formatar(digitos) {
    if (digitos.length !== 20)
        return digitos;
    return (`${digitos.slice(0, 7)}-${digitos.slice(7, 9)}.${digitos.slice(9, 13)}` +
        `.${digitos.slice(13, 14)}.${digitos.slice(14, 16)}.${digitos.slice(16, 20)}`);
}
/**
 * Interpreta um número de processo escrito em qualquer formato.
 *
 * Números com dígito verificador incorreto ainda são aceitos — a planilha do
 * usuário pode conter um erro de digitação que o DataJud mesmo assim resolve —
 * mas o resultado sinaliza isso em `digitoVerificadorValido` para que a
 * interface possa avisar.
 */
export function interpretarNumeroProcesso(entrada) {
    const bruto = (entrada ?? "").trim();
    if (bruto === "") {
        return { ok: false, erro: "Número do processo em branco." };
    }
    const digitos = apenasDigitos(bruto);
    if (digitos.length === 0) {
        return { ok: false, erro: "Nenhum dígito encontrado no número informado." };
    }
    if (digitos.length !== 20) {
        return {
            ok: false,
            erro: `O número CNJ tem 20 dígitos; foram encontrados ${digitos.length}.`,
        };
    }
    const segmento = digitos.slice(13, 14);
    const codigoTribunal = digitos.slice(14, 16);
    const tribunais = tribunaisCandidatos(segmento, codigoTribunal);
    return {
        ok: true,
        numero: {
            digitos,
            formatado: formatar(digitos),
            sequencial: digitos.slice(0, 7),
            digitoVerificador: digitos.slice(7, 9),
            ano: Number(digitos.slice(9, 13)),
            segmento,
            codigoTribunal,
            unidadeOrigem: digitos.slice(16, 20),
            digitoVerificadorValido: calcularDigitoVerificador(digitos) === digitos.slice(7, 9),
            tribunais,
        },
    };
}
/**
 * Extrai números de processo de um texto livre — uma célula de planilha pode
 * trazer o número junto de outras informações.
 *
 * Reconhece tanto a forma mascarada quanto 20 dígitos seguidos, e ignora
 * sequências mais longas (um CPF colado a um número, por exemplo).
 */
const PADRAO_NUMERO = /(?<!\d)(\d{7})-?(\d{2})\.?(\d{4})\.?(\d)\.?(\d{2})\.?(\d{4})(?!\d)/g;
export function extrairNumeros(texto) {
    const encontrados = [];
    for (const m of texto.matchAll(PADRAO_NUMERO)) {
        encontrados.push(m.slice(1).join(""));
    }
    return encontrados;
}
//# sourceMappingURL=numero-processo.js.map